import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useExportImport({ exportUrl, importUrl, importPdfUrl, companyId, onImportSuccess, entityName }) {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingPDF, setImportingPDF] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      const response = await axios.get(`${API_URL}${exportUrl}`, {
        params,
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityName || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = {};
      if (companyId) params.company_id = companyId;
      const res = await axios.post(`${API_URL}${importUrl}`, formData, {
        params,
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, errors } = res.data;
      if (imported > 0) {
        toast.success(`Imported ${imported} record${imported > 1 ? 's' : ''}`);
        onImportSuccess?.();
      }
      if (errors?.length > 0) {
        toast.error(`${errors.length} row(s) had errors`);
      }
      if (imported === 0 && (!errors || errors.length === 0)) {
        toast.info('No records found in CSV');
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleImportPDF = importPdfUrl ? async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportingPDF(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = {};
      if (companyId) params.company_id = companyId;
      const res = await axios.post(`${API_URL}${importPdfUrl}`, formData, {
        params,
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, errors } = res.data;
      if (imported > 0) {
        toast.success(`Imported ${imported} expense${imported > 1 ? 's' : ''} from PDF`);
        onImportSuccess?.();
      }
      if (errors?.length > 0) {
        toast.error(`${errors.length} row(s) had errors`);
      }
      if (imported === 0 && (!errors || errors.length === 0)) {
        toast.info('No expense data found in PDF. Ensure the PDF has a table with Description and Amount columns.');
      }
    } catch (error) {
      console.error('PDF import failed:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to import PDF');
    } finally {
      setImportingPDF(false);
      event.target.value = '';
    }
  } : null;

  return { handleExportCSV, handleImportCSV, handleImportPDF, exporting, importing, importingPDF };
}
